// Seller Dashboard → Shipped History page. Read-only view of the seller's
// past shipments split across four tabs: Shipped / Delivered / Cancelled /
// Returned. All data comes from listShippedHistory (seller-scoped server
// function). No order/shipment/Stripe/payout mutations happen here.
import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  ArrowLeft,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Copy,
  Download,
  ExternalLink,
  ImageOff,
  Package,
  RefreshCcw,
  RotateCcw,
  Search,
  Truck,
  XCircle,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { RouteErrorBoundary, RouteNotFoundBoundary } from "@/components/route-boundaries";
import { formatGBP } from "@/lib/format";
import { getLabelSignedUrl } from "@/lib/seller-fulfilment.functions";
import {
  listShippedHistory,
  royalMailTrackingUrl,
  type ShippedHistoryRow,
  type ShippedHistoryTab,
} from "@/lib/shipped-history.functions";

export const Route = createFileRoute("/_authenticated/seller/shipped")({
  head: () => ({
    meta: [
      { title: "Shipped History — Seller Dashboard" },
      {
        name: "description",
        content: "Review your shipped, delivered, cancelled and returned orders.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ShippedHistoryPage,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: RouteNotFoundBoundary,
});

const PAGE_SIZE = 50;

const TABS: Array<{ id: ShippedHistoryTab; label: string; emoji: string; icon: typeof Package }> = [
  { id: "shipped", label: "Shipped Orders", emoji: "📦", icon: Package },
  { id: "delivered", label: "Delivered", emoji: "✅", icon: CheckCircle2 },
  { id: "cancelled", label: "Cancelled", emoji: "❌", icon: XCircle },
  { id: "returned", label: "Returned", emoji: "↩️", icon: RotateCcw },
];

function ShippedHistoryPage() {
  const [tab, setTab] = useState<ShippedHistoryTab>("shipped");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [buyer, setBuyer] = useState("");
  const [tracking, setTracking] = useState("");
  const [auctionTitle, setAuctionTitle] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  // Debounce instant search to keep the DB hits reasonable.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  // Reset page when the tab or any filter changes.
  useEffect(() => {
    setPage(1);
  }, [tab, debouncedSearch, buyer, tracking, auctionTitle, from, to]);

  const fetchHistory = useServerFn(listShippedHistory);
  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: [
      "seller-shipped-history",
      tab,
      page,
      debouncedSearch,
      buyer,
      tracking,
      auctionTitle,
      from,
      to,
    ],
    queryFn: () =>
      fetchHistory({
        data: {
          tab,
          page,
          pageSize: PAGE_SIZE,
          search: debouncedSearch || undefined,
          buyer: buyer.trim() || undefined,
          tracking: tracking.trim() || undefined,
          auctionTitle: auctionTitle.trim() || undefined,
          from: from ? new Date(from).toISOString() : undefined,
          to: to ? new Date(`${to}T23:59:59`).toISOString() : undefined,
        },
      }),
    placeholderData: keepPreviousData,
  });

  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE));

  const clearFilters = () => {
    setSearch("");
    setBuyer("");
    setTracking("");
    setAuctionTitle("");
    setFrom("");
    setTo("");
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10 space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-gold">Seller</p>
          <h1 className="mt-1 font-display text-4xl sm:text-5xl">
            <span className="text-gold-gradient">Shipped History</span>
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Track past shipments, delivery outcomes and returns. Read-only.
          </p>
        </div>
        <Button asChild variant="outline" size="sm">
          <Link to="/seller">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to dashboard
          </Link>
        </Button>
      </header>

      <Tabs value={tab} onValueChange={(v) => setTab(v as ShippedHistoryTab)}>
        <TabsList className="flex flex-wrap h-auto">
          {TABS.map((t) => (
            <TabsTrigger key={t.id} value={t.id}>
              <span aria-hidden className="mr-1.5">
                {t.emoji}
              </span>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="rounded-xl border border-border/60 bg-card p-4 sm:p-5 space-y-4">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by buyer, auction title or tracking number…"
            className="pl-9"
            aria-label="Search shipped orders"
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <FilterField label="Buyer" value={buyer} onChange={setBuyer} placeholder="Buyer name" />
          <FilterField
            label="Tracking #"
            value={tracking}
            onChange={setTracking}
            placeholder="Royal Mail tracking"
          />
          <FilterField
            label="Auction"
            value={auctionTitle}
            onChange={setAuctionTitle}
            placeholder="Auction title"
          />
          <FilterField label="From" value={from} onChange={setFrom} type="date" />
          <FilterField label="To" value={to} onChange={setTo} type="date" />
        </div>

        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-xs text-muted-foreground">
            {data ? `${data.total} result${data.total === 1 ? "" : "s"}` : ""}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Clear filters
            </Button>
            <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
              <RefreshCcw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
              <span className="ml-2">Refresh</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-border/60 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[80px]">Order</TableHead>
                <TableHead className="min-w-[220px]">Auction</TableHead>
                <TableHead>Buyer</TableHead>
                <TableHead>Tracking</TableHead>
                <TableHead>Service</TableHead>
                <TableHead>Royal Mail</TableHead>
                <TableHead className="whitespace-nowrap">Dispatched</TableHead>
                <TableHead className="whitespace-nowrap">Delivered</TableHead>
                <TableHead className="text-right whitespace-nowrap">Total</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <SkeletonRows />
              ) : isError ? (
                <TableRow>
                  <TableCell colSpan={11} className="py-10 text-center text-sm">
                    <p className="text-red-400">Couldn&apos;t load shipped history.</p>
                    <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
                      Retry
                    </Button>
                  </TableCell>
                </TableRow>
              ) : !data?.rows.length ? (
                <TableRow>
                  <TableCell colSpan={11} className="py-10 text-center text-sm text-muted-foreground">
                    No matching orders.
                  </TableCell>
                </TableRow>
              ) : (
                data.rows.map((r) => <HistoryRow key={r.orderId} row={r} tab={tab} />)
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {data && data.total > PAGE_SIZE && (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-xs text-muted-foreground">
            Page {page} of {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || isFetching}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || isFetching}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------------------- helpers ------------------------------ */

function FilterField({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  const id = useMemo(() => `f-${label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`, [label]);
  return (
    <div>
      <Label htmlFor={id} className="text-xs text-muted-foreground">
        {label}
      </Label>
      <Input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1"
      />
    </div>
  );
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 6 }).map((_, i) => (
        <TableRow key={i}>
          <TableCell colSpan={11}>
            <div className="h-8 rounded bg-muted/30 animate-pulse" aria-hidden />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

function HistoryRow({ row, tab }: { row: ShippedHistoryRow; tab: ShippedHistoryTab }) {
  const getLabel = useServerFn(getLabelSignedUrl);

  const copyTracking = async () => {
    if (!row.trackingNumber) return;
    try {
      await navigator.clipboard.writeText(row.trackingNumber);
      toast.success("Tracking number copied.");
    } catch {
      toast.error("Could not copy tracking number.");
    }
  };

  const downloadLabel = async () => {
    try {
      const res = await getLabel({ data: { orderId: row.orderId } });
      if (res?.url) window.open(res.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Label unavailable.");
    }
  };

  return (
    <TableRow>
      <TableCell className="font-mono text-xs whitespace-nowrap">{row.reference}</TableCell>
      <TableCell className="min-w-[220px]">
        <div className="flex items-center gap-3">
          {row.cardImage ? (
            <img
              src={row.cardImage}
              alt=""
              loading="lazy"
              className="h-10 w-10 rounded object-cover border border-border/40"
            />
          ) : (
            <div className="h-10 w-10 rounded bg-surface-2 flex items-center justify-center" aria-hidden>
              <ImageOff className="h-4 w-4 text-muted-foreground" />
            </div>
          )}
          <span className="text-sm text-foreground line-clamp-2">{row.auctionTitle ?? "—"}</span>
        </div>
      </TableCell>
      <TableCell className="text-sm">{row.buyerName ?? "—"}</TableCell>
      <TableCell>
        {row.trackingNumber ? (
          <a
            href={royalMailTrackingUrl(row.trackingNumber)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-gold hover:underline font-mono text-xs"
            aria-label={`Track ${row.trackingNumber} on Royal Mail`}
          >
            {row.trackingNumber}
            <ExternalLink className="h-3 w-3" aria-hidden />
          </a>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="text-xs">{row.serviceLabel ?? "—"}</TableCell>
      <TableCell>
        <RoyalMailBadge shipment={row.shipmentStatus} order={row.orderStatus} tab={tab} />
      </TableCell>
      <TableCell className="whitespace-nowrap text-xs">
        {row.dispatchedAt ? new Date(row.dispatchedAt).toLocaleDateString() : "—"}
      </TableCell>
      <TableCell className="whitespace-nowrap text-xs">
        {row.deliveredAt ? new Date(row.deliveredAt).toLocaleDateString() : "—"}
      </TableCell>
      <TableCell className="text-right tabular-nums whitespace-nowrap">
        {formatGBP(row.amountPence)}
      </TableCell>
      <TableCell>
        <OrderStatusBadge status={row.orderStatus} />
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1">
          <Button asChild variant="ghost" size="sm" title="View order">
            <Link to="/orders" search={{ status: row.orderStatus }}>
              <Truck className="h-4 w-4" />
              <span className="sr-only">View order</span>
            </Link>
          </Button>
          {row.trackingNumber && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={copyTracking}
                title="Copy tracking number"
                aria-label="Copy tracking number"
              >
                <Copy className="h-4 w-4" />
              </Button>
            </>
          )}
          {row.hasLabel && (
            <Button
              variant="ghost"
              size="sm"
              onClick={downloadLabel}
              title="Download shipping label"
              aria-label="Download shipping label"
            >
              <Download className="h-4 w-4" />
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

/** Map raw shipment/order status to friendly Royal Mail badge. */
export function friendlyRoyalMailStatus(
  shipmentStatus: string | null,
  orderStatus: string,
  tab: ShippedHistoryTab,
): { label: string; tone: "sky" | "indigo" | "emerald" | "amber" | "rose" | "slate" } {
  if (tab === "cancelled" || orderStatus === "cancelled")
    return { label: "Cancelled", tone: "slate" };
  if (tab === "returned" || shipmentStatus === "returned")
    return { label: "Returned", tone: "rose" };
  if (orderStatus === "delivered" || orderStatus === "released")
    return { label: "Delivered", tone: "emerald" };
  switch (shipmentStatus) {
    case "label_created":
      return { label: "Label Created", tone: "sky" };
    case "dispatched":
      return { label: "Dispatched", tone: "indigo" };
    case "in_transit":
      return { label: "In Transit", tone: "indigo" };
    case "delivered":
      return { label: "Delivered", tone: "emerald" };
    case "delivery_attempted":
      return { label: "Delivery Attempted", tone: "amber" };
    default:
      // Fall back to a generic label rather than exposing raw enum text.
      if (orderStatus === "shipped") return { label: "Dispatched", tone: "indigo" };
      return { label: "—", tone: "slate" };
  }
}

function RoyalMailBadge({
  shipment,
  order,
  tab,
}: {
  shipment: string | null;
  order: string;
  tab: ShippedHistoryTab;
}) {
  const { label, tone } = friendlyRoyalMailStatus(shipment, order, tab);
  const cls: Record<string, string> = {
    sky: "bg-sky-500/15 text-sky-300 border-sky-500/30",
    indigo: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
    emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    amber: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    rose: "bg-rose-500/15 text-rose-300 border-rose-500/30",
    slate: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  };
  return (
    <Badge variant="outline" className={cls[tone]}>
      {label}
    </Badge>
  );
}

function OrderStatusBadge({ status }: { status: string }) {
  const friendly: Record<string, { label: string; tone: string }> = {
    shipped: { label: "Shipped", tone: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30" },
    delivered: { label: "Delivered", tone: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
    released: { label: "Completed", tone: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
    cancelled: { label: "Cancelled", tone: "bg-slate-500/15 text-slate-300 border-slate-500/30" },
    refunded: { label: "Refunded", tone: "bg-rose-500/15 text-rose-300 border-rose-500/30" },
  };
  const f = friendly[status] ?? { label: "—", tone: "bg-slate-500/15 text-slate-300 border-slate-500/30" };
  return (
    <Badge variant="outline" className={f.tone}>
      {f.label}
    </Badge>
  );
}
